import {
  isLibraryInOut,
  isTemplateCreateOperation,
  isTemplateHideOperation,
} from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, notEmpty } from '@festina-life/core'
import type { SearchMatch } from '@festina-life/ui/search'
import { useCallback, useMemo } from 'react'

import { getTemplateOperationName } from '../../uiModels/templateOperations/helpers'
import type { IndexedTemplateOperationWithMetaData } from '../../uiModels/templateOperations/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import { configurationItemsMapToList } from '../errorsPanel/helpers'
import {
  expressionMatchesForQueryInTemplateOperation,
  groupSearchResults,
  propertyMatchesForQueryInOperation,
} from './helpers'
import type {
  ConfigurationItemSearchFunctions,
  ConfigurationItemSearchMatch,
  ConfigurationItemSearchResult,
  SearchCategoryKey,
} from './types'

const toConfigurationItemSearchResult =
  (matches?: SearchMatch<ConfigurationItemSearchMatch, SearchCategoryKey>[]) =>
  (
    operationAtDate: IndexedTemplateOperationWithMetaData,
  ): ConfigurationItemSearchResult => {
    const templateId = getTemplateOperationName(
      operationAtDate.internalOperation,
    )
    assertNotEmpty(templateId)

    return {
      value: {
        id: templateId,
        type: isTemplateCreateOperation(operationAtDate?.internalOperation)
          ? operationAtDate.internalOperation.entity?.modelTypeName
          : undefined,
        operationType:
          !isTemplateHideOperation(operationAtDate.internalOperation) &&
          isLibraryInOut(operationAtDate.internalOperation.entity)
            ? 'library'
            : 'template',
        isDraft: operationAtDate.isDraft,
        packageName: !isTemplateHideOperation(operationAtDate.internalOperation)
          ? operationAtDate.internalOperation.entity?.packageName
          : operationAtDate.internalOperation.packageName,
        layer: operationAtDate.layer,
        block: operationAtDate.block,
      },
      matches,
    }
  }

export const useTemplateSearch = (): ConfigurationItemSearchFunctions => {
  const { templateOperations } = useOperationsContext()
  const allOperations = useMemo(
    () =>
      configurationItemsMapToList(templateOperations)
        .flatMap((indexedOperation) => indexedOperation.operations)
        .filter((operation) => !operation.annulOperations.length),
    [templateOperations],
  )

  const searchByTemplateName = useCallback(
    (query: string) => {
      const needle = query.toLowerCase().trim()

      return Promise.resolve(
        allOperations
          .filter(
            (operationAtDate) =>
              !!getTemplateOperationName(operationAtDate?.internalOperation)
                ?.toLocaleLowerCase()
                .includes(needle),
          )
          .map(toConfigurationItemSearchResult()),
      )
    },
    [allOperations],
  )

  const searchByClass = useCallback(
    (query: string) => {
      const needle = query.toLowerCase().trim()

      return Promise.resolve(
        allOperations
          .filter(
            (operationAtDate) =>
              isTemplateCreateOperation(operationAtDate?.internalOperation) &&
              (!!operationAtDate?.internalOperation.entity?.modelTypeName
                ?.toLocaleLowerCase()
                .includes(needle) ||
                !!operationAtDate?.internalOperation.entity?.templateTypeName
                  ?.toLocaleLowerCase()
                  .includes(needle)),
          )
          .map(toConfigurationItemSearchResult()),
      )
    },
    [allOperations],
  )

  const searchByProperties = useCallback(
    (query: string) => {
      const needle = query.toLowerCase().trim()

      return Promise.resolve(
        allOperations
          .filter(notEmpty)
          .map((operationAtDate) =>
            propertyMatchesForQueryInOperation(operationAtDate, needle),
          )
          .filter(([, matches]) => matches.length > 0)
          .map(([operation, matches]) =>
            toConfigurationItemSearchResult(matches)(operation),
          ),
      )
    },
    [allOperations],
  )

  const searchByFPlus = useCallback(
    (query: string) => {
      const needle = query.toLowerCase().trim()

      return Promise.resolve(
        allOperations
          .filter(notEmpty)
          .map((operationAtDate) =>
            expressionMatchesForQueryInTemplateOperation(
              operationAtDate,
              needle,
            ),
          )
          .filter(([, matches]) => matches.length > 0)
          .map(([operation, matches]) =>
            toConfigurationItemSearchResult(matches)(operation),
          ),
      )
    },
    [allOperations],
  )

  const searchAll = useCallback(
    async (query: string) => {
      const allByName = await searchByTemplateName(query)
      const allByClass = await searchByClass(query)
      const allByProperties = await searchByProperties(query)
      const allByFPlus = await searchByFPlus(query)

      return groupSearchResults(
        ...allByName,
        ...allByClass,
        ...allByProperties,
        ...allByFPlus,
      )
    },
    [searchByClass, searchByFPlus, searchByProperties, searchByTemplateName],
  )

  return {
    name: searchByTemplateName,
    className: searchByClass,
    properties: searchByProperties,
    fplus: searchByFPlus,
    all: searchAll,
  }
}
